import i18next from "i18next";
import moment from "moment";
import "./calendar.css";
import { Calendar, momentLocalizer, CalendarProps } from "react-big-calendar";
import { CalendarToolbar } from "./CalendarToolbar/CalendarToolbar";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactNode, useState } from "react";

interface IGeneralCalendar extends Omit<CalendarProps, "localizer"> {
  extraCellContent?: (date: Date) => ReactNode;
  calendarKey?: string;
  customCellClassname?: (date: Date, label: string) => string;
  customHeadClassname?: (date: Date, label: string) => string;
  onSelectedDate?: (date: string) => void;
  onDeselectedDate?: () => void;
  onCellClick?: (date: string) => void;
  withDeselect?: boolean;
  isFull?: boolean;
}
const GeneralCalendar: React.FC<IGeneralCalendar> = ({
  components,
  views,
  extraCellContent,
  className,
  calendarKey,
  customCellClassname,
  customHeadClassname,
  onSelectedDate,
  onCellClick,
  withDeselect = false,
  onDeselectedDate,
  isFull,
  ...props
}) => {
  moment.locale(i18next.language);
  const localizer = momentLocalizer(moment);

  const location = useLocation();
  const currentLocationParams = new URLSearchParams(location.search);
  const selectedDate =
    currentLocationParams.get(`${calendarKey}-Date`) ?? undefined;

  const [calendarSelectedDate, setCalendarSelectedDate] = useState<
    string | undefined
  >(selectedDate);

  const navigate = useNavigate();
  return (
    <div className={`${isFull ? "w-full" : "w-fit"} mx-auto`}>
      <Calendar
        className={`h-[26rem] w-full rounded-[16px] px-2 pb-[24px] lg:w-[20rem] ${className} `}
        selectable
        localizer={localizer}
        components={{
          toolbar: CalendarToolbar,
          month: {
            dateHeader: ({ date, label }) => {
              const isSelected =
                calendarSelectedDate &&
                moment(date).isSame(moment(calendarSelectedDate), "day");
              return (
                <div
                  onClick={() => {
                    if (
                      withDeselect &&
                      calendarSelectedDate &&
                      moment(date).isSame(moment(calendarSelectedDate), "day")
                    ) {
                      setCalendarSelectedDate(undefined);
                      if (calendarKey) {
                        currentLocationParams.delete(`${calendarKey}-Date`);
                        const queryString = currentLocationParams.toString();
                        navigate(`?${queryString}`);
                      } else {
                        onDeselectedDate && onDeselectedDate();
                      }
                    } else {
                      const newDate = `${moment(date).locale("en").format("YYYY-MM-DD")}`;
                      setCalendarSelectedDate(newDate);
                      if (calendarKey) {
                        currentLocationParams.set(
                          `${calendarKey}-Date`,
                          newDate,
                        );
                        const queryString = currentLocationParams.toString();
                        navigate(`?${queryString}`);
                      } else {
                        onSelectedDate && onSelectedDate(newDate);
                      }
                      onCellClick && onCellClick(newDate);
                    }
                  }}
                  className={`text-primary-textFont flex h-full w-full cursor-pointer flex-col items-center justify-center pb-4 pt-4 ${isSelected ? "isSelected rounded-full bg-primary font-bold text-white" : ""} ${customCellClassname ? customCellClassname(date, label) : ""}`}
                >
                  <h3 className="text-sm">{label}</h3>
                  {extraCellContent && extraCellContent(date)}
                </div>
              );
            },
            header: ({ date, label }) => (
              <div
                className={` ${customHeadClassname && customHeadClassname(date, label)}`}
              >
                {label}
              </div>
            ),
          },
          ...components,
        }}
        views={{
          month: true,
          ...views,
        }}
        {...props}
      />
    </div>
  );
};

export default GeneralCalendar;
